import type { ReactNode } from 'react';
import { EmptyState, Modal } from '../../components/ui/common';
import { Icon } from '../../components/ui/icon';
import { errorMessage } from '../../lib/api';
import { copyText } from './format';

/** Confirm dialog with the mockup's actions: [danger/primary action, cancel]. */
export function ConfirmModal(props: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  tone?: 'danger' | 'primary';
  pending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
}) {
  return (
    <Modal
      open={props.open}
      title={props.title}
      onClose={props.onClose}
      footer={
        <>
          <button
            className={`btn ${props.tone === 'primary' ? 'primary' : 'danger solid'}`}
            type="button"
            onClick={props.onConfirm}
            disabled={props.pending}
          >
            {props.confirmLabel}
          </button>
          <button className="btn" type="button" onClick={props.onClose}>
            {props.cancelLabel ?? 'ביטול'}
          </button>
        </>
      }
    >
      {props.children}
    </Modal>
  );
}

/** Inline error panel for a failed query, with retry. */
export function QueryError({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <div className="callout danger" role="alert">
      <Icon name="alert" />
      <div>
        <b>לא הצלחנו לטעון.</b> {errorMessage(error)}
      </div>
      <div className="callout-actions">
        <button className="btn sm" type="button" onClick={onRetry}>
          <Icon name="refresh" />
          ניסיון נוסף
        </button>
      </div>
    </div>
  );
}

export function NoAccess({ what }: { what: string }) {
  return <EmptyState icon="shield" title="אין לך הרשאה למסך הזה" text={`צפייה ב${what} דורשת הרשאה. פונים למנהל־על אם צריך גישה.`} />;
}

/** Shown when the server returns devInviteUrl (outside production, mail not sent). */
export function DevInviteLink({ url }: { url: string }) {
  return (
    <div className="callout warn mt">
      <Icon name="link" />
      <div style={{ minWidth: 0 }}>
        <b>המייל לא נשלח (סביבת פיתוח).</b> מעבירים את הקישור ידנית:
        <div className="st-mono mt-s">{url}</div>
        <button className="btn sm mt-s" type="button" onClick={() => copyText(url, 'הקישור הועתק')}>
          <Icon name="copy" />
          העתקת הקישור
        </button>
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 4 }: { rows?: number }) {
  return (
    <div className="panel-body" aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="skel" style={{ height: 38, marginBottom: 10 }} />
      ))}
    </div>
  );
}
